import { grid, type Part, type Recolor } from './body';

/**
 * Outfits (ADR-0090): what the gardener wears above the waist, over trousers in the team colour (the legs, body.ts), so
 * the team reads whatever the outfit, with the hat's band and ribbon. Each is its standing top (drawn with the torso's
 * bob, under the face) and its lying top (right of the dead pose's legs, under the head).
 *
 * Recoloured by the skin and the team together (index.ts), so an open collar shows the look's own skin: the cloth
 * keeps off the skin's keys (`s S n N i t`) and the team's (`b B c`) but where it means them.
 *
 * Rubric (looks sheet, every outfit on four skins and three teams idle, run and dead, 1:1 on grass and lane, the picker
 * and a 5v5 in the browser): hoodie S4 R4 P5 L4 C4 A4 F4, shirt S4 R5 P5 L4 C4 A4 F5. Still weak: ENDESGA has no neutral
 * grey, so the charcoal is its blue slate and black hair on the hood reads as one mass; the shirt's collar is a line at
 * 1:1; the dead pose shows a column of any outfit at the waist, as it did of the tee.
 */
export interface Outfit {
  torso: Part;
  dead: Part;
  /**
   * A suit's trousers and boots (ADR-0112): a recolour of the legs' keys (near leg `c b B`, boots `n N m`; standing,
   * running and lying), applied before the team's, so a key mapped to `b B c` still takes the team colour. None: the
   * team's trousers.
   */
  legs?: Recolor;
}

// White tee (chin shadow at the collar) under team bib overalls: straps with grey buttons, a pocket with a lit hem.
const OVERALLS = grid(`
  ....55wwcbb55444444444455bbB5544....
  ..55wwwwcbbwww55555555wwwbbB555444..
  .55wwwwwcbbwwwwwwwwwwwwwwbbB5555444.
  55wwwww5cbbwwwwwwwwwwwwwwbbB45555444
  45555545cbb5wwwwwwwwwwww5bbB45555544
  .344444355ccccccccccccccc55B3444443.
  ....cbbb43bbbbbbbbbbbbbbb43bbbBB....
  ....cbbbbbbbccccccccbbbbbbbbbBBB....
  ....cbbbbbbbcbbbbbbBbbbbbbbbbBBB....
  ....cbbbbbbbcbbbbbbBbbbbbbbbbBBB....
  ....cbbbbbbbBBBBBBBBbbbbbbbbbBBB....
  ....cbbbbbbbbbbbbbbbbbbbbbbbbBBB....
`);

// The bib lying: its lit waist seam (left), the pocket turned, the tee's collar under the chin and a sleeve either side.
const DEAD_OVERALLS = grid(`
  ....wwww
  ...wwwww
  ccccccc5
  cbbbbb5w
  cbbbbbbw
  cbcccbbw
  cbcbbbbw
  cbcbbbbw
  cbBBBbb5
  cbbbbbb5
  cbbbbbb5
  cbbbbbb5
  cbbbbbb5
  BBBBBBB5
  BBBBBB5.
  BBBBBBB5
  BBBB5555
  ....4444
`);

// A charcoal hoodie: the hood bunched behind the neck (a soft roll lit on its rim, over the far shoulder, its lining
// dark under the jaw, which the run's lean shows), the neckline in shadow under the chin, drawstrings with light tips,
// shoulders lit from the top-left, a kangaroo pocket with a lit fold and slanted openings, a ribbed hem at the waist.
const HOODIE = grid(`
  ...33322.................................
  .333222221...............................
  33222222221..............................
  3222222222111k...........................
  1222222222111111k1.......................
  ..11222222211333111111111111111222211....
  ....11111122222233111111111111222222111..
  ......3332222222223311111111222222222111.
  .....332222212222222431111242222212222111
  .....122222212222222422112242222212221111
  ......1111111222222252222225222221111111.
  ........22222221111111111111111222111....
  ........222222133333322222222221222111...
  ........22222132222222222222222122111....
  .........1111111111111111111111111111....
  .........2121212121212121212121211111....
`);

// The hoodie lying: its ribbed hem at the waist (left), the body under the head, a sleeve either side.
const DEAD_HOODIE = grid(`
  ....3322
  ...33222
  23333332
  12222222
  22222222
  12222222
  22222222
  12222222
  22222222
  12222222
  22222222
  12222222
  22222222
  11111112
  1111111.
  11111111
  11112222
  ....1111
`);

// A white collared shirt, the collar open on the skin (`s S`, the look's), a placket with buttons, rolled sleeves,
// tucked into the trousers under a dark belt with a light buckle.
const SHIRT = grid(`
  ....55wwwwwww5ww4SSSSS4ww5ww5544....
  ..55wwwwwwwwww5ww4sss4ww5www555444..
  .55wwwwwwwwwwww5ww4s4ww5wwww5555444.
  55wwwww5wwwwwwww5554555wwwww45555444
  45555545wwwwwwwwwww5wwwwwww545555544
  .34444435wwwwwwwwww4wwwwww553444443.
  ....5wwwwwwwwwwwwww5wwwwwww55544....
  ....4555555555555554555555555444....
  ....2222222222222255422222222211....
  ....1111111111111144411111111111....
`);

// The shirt lying: the belt at the waist (left) with its buckle, the body under the head, a sleeve either side.
const DEAD_SHIRT = grid(`
  ....wwww
  ...wwwww
  1wwwwww5
  1wwwww5w
  1wwwwwww
  1wwwwwww
  1wwwwwww
  1wwwwwww
  5wwwwww5
  4wwwwww5
  1wwwwww5
  1wwwwww5
  1wwwwww5
  1555555w
  155555w.
  15555555
  15555555
  ....4444
`);

const DEAD_AT = [24, 51] as const;

/** The outfits by content key (`LOOKS.outfit`), the first the default: the tee and overalls the gardener was drawn in. */
export const OUTFITS = {
  overalls: { torso: [OVERALLS, 12, 44], dead: [DEAD_OVERALLS, ...DEAD_AT] },
  hoodie: { torso: [HOODIE, 7, 39], dead: [DEAD_HOODIE, ...DEAD_AT] },
  shirt: { torso: [SHIRT, 12, 44], dead: [DEAD_SHIRT, ...DEAD_AT] },
} satisfies Record<string, Outfit>;
